package com.xorrus.trading.lot.relayclient.model

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlin.time.Instant

/**
 * Zero DTE Opening Put 2.1 — Remote-Typisierung analog MMS (Spec §3-Muster).
 *
 * Underlying ist eine fixe Strategie-Konstante (SPY), ebenso Zeitfenster,
 * Zielpreisbereich (0.6..0.8) und SL — die Remote-Params spiegeln exakt das,
 * was auch die Desktop-View anbietet: Konto, Anzahl, Transmit, Vorbedingungen
 * ignorieren und optional auf das Handelsfenster warten.
 */

val OPENING_PUT_STRATEGY_ID = StrategyId("openingPut21")
const val OPENING_PUT_SCHEMA_VERSION = 1

@Serializable
@SerialName("openingPut21")
data class OpeningPutParams(
    /** Konto (dynamische Auswahl, [OpeningPutOptions]) */
    val accountId: AccountId,
    /** An Börse übertragen */
    val transmitToExchange: Boolean,
    /** Anzahl 1..1000 (Grenze der Strategie) */
    val quantity: Int,
    /**
     * PreCheck (Wochentag/Zeitfenster, Move Up) übergehen — desktopseitig zusätzlich
     * an die Lizenz FEATURE_IGNORE_PRECONDITION gebunden (wie in der Desktop-View).
     * Teaminterne Funktion, nicht für Kunden.
     */
    val ignorePreconditions: Boolean = false,
    /**
     * erst bis 10:30 NY warten (Pendant zum „zeitgesteuert starten"-Button der View) —
     * desktopseitig an FEATURE_TIME_BASED_TRADING gebunden. Teaminterne Funktion,
     * nicht für Kunden; platziert ohne erneute Kontrakt-Bestätigung.
     */
    val waitForWindow: Boolean = false,
    /**
     * Vom Nutzer bestätigter Optionskontrakt in Symbol-Notation, aus `strategy.preview`
     * ([OpeningPutPreview.contract]). PFLICHT für den Sofort-Start (waitForWindow = false):
     * Der Desktop ermittelt den Kontrakt beim Platzieren erneut aus Live-Preisen und
     * platziert nur bei Übereinstimmung (Compliance). Bei waitForWindow = true ignoriert.
     * Nicht in Defaults/Presets persistieren — die Bestätigung gilt nur für den einen Start.
     */
    val expectedContract: String? = null,
    override val schemaVersion: Int = OPENING_PUT_SCHEMA_VERSION,
) : StrategyParams

@Serializable
@SerialName("openingPut21")
data class OpeningPutOptions(
    val accounts: List<AccountRef>,
    override val asOf: Instant,
    /** Lizenz-Freigaben der teaminternen Schalter ([ChecklistGates]); Default gesperrt. */
    override val ignorePreconditionsAllowed: Boolean = false,
    override val waitForWindowAllowed: Boolean = false,
) : StrategyOptions, ChecklistGates

/**
 * Ergebnis von `strategy.preview` (Compliance-Vorschau für den Sofort-Start): der aus
 * Live-Preisen ermittelte Kontrakt plus alle Orders, die platziert würden. Der Remote-
 * Nutzer bestätigt genau diesen Kontrakt; die Bestätigung wandert als
 * [OpeningPutParams.expectedContract] in `strategy.activate`, wo der Desktop beim
 * Platzieren erneut ermittelt und nur bei Übereinstimmung platziert. KEIN Cache —
 * jede Vorschau rechnet frisch.
 */
@Serializable
data class OpeningPutPreview(
    /** Symbol-Notation des ermittelten Kontrakts; null = Ermittlung fehlgeschlagen (siehe [message]). */
    val contract: String? = null,
    /** Anzeigefertige Zeile pro Order (Entry, Stop Loss, Time-Exit). */
    val orders: List<String> = emptyList(),
    /** Fehlermeldung, wenn kein Kontrakt ermittelt werden konnte. */
    val message: String? = null,
    /** Zweisprachige Fassung von [message]; null bei alten Desktops. */
    val messageText: RemoteText? = null,
    val asOf: Instant,
)

// ---- Status-Projektion: Checklist-Strategie ---------------------------------------

@Serializable
enum class RemoteStepState { UNCHECKED, SUCCESS, WARNING, ERROR }

/** Ein PreCheck-Schritt aus `ChecklistStrategyState` (Label + Zustand + Meldung). */
@Serializable
data class RemoteChecklistStep(
    val label: String,
    val state: RemoteStepState = RemoteStepState.UNCHECKED,
    val message: String? = null,
    /** Zweisprachige Fassungen von [label]/[message]; null bei alten Desktops. */
    val labelText: RemoteText? = null,
    val messageText: RemoteText? = null,
)

@Serializable
@SerialName("openingPut21")
data class OpeningPutStatus(
    override val phase: StrategyPhase,
    override val activeParams: StrategyParams? = null,
    override val since: Instant? = null,
    override val message: String? = null,
    override val messageText: RemoteText? = null,
    val steps: List<RemoteChecklistStep> = emptyList(),
    /** PreCheck wurde für diesen Lauf übersprungen */
    val preconditionsIgnored: Boolean = false,
) : RemoteStrategyStatus {
    override val strategyId: StrategyId get() = OPENING_PUT_STRATEGY_ID
}

// ---- Bounds -------------------------------------------------------------------------

data class OpeningPutBounds(
    val maxQuantity: Int? = 10,
    val allowTransmitToExchange: Boolean = false,
) : ParamsBounds<OpeningPutParams> {

    override fun violations(params: OpeningPutParams): List<String> = violationsText(params).map { it.de }

    override fun violationsText(params: OpeningPutParams): List<RemoteText> {
        val violations = mutableListOf<RemoteText>()
        if (maxQuantity != null && params.quantity > maxQuantity) {
            violations += RemoteText(
                de = "quantity ${params.quantity} überschreitet das Limit $maxQuantity",
                en = "quantity ${params.quantity} exceeds the limit $maxQuantity",
            )
        }
        if (params.transmitToExchange && !allowTransmitToExchange) {
            violations += RemoteText(
                de = "transmitToExchange ist für Remote-Aktivierungen nicht freigegeben",
                en = "transmitToExchange is not enabled for remote activations",
            )
        }
        return violations
    }
}

// ---- Geteilte Validierung (§3.2) ------------------------------------------------------

fun validateOpeningPut(params: OpeningPutParams, options: OpeningPutOptions?): List<ValidationError> {
    val errors = mutableListOf<ValidationError>()

    if (params.schemaVersion != OPENING_PUT_SCHEMA_VERSION) {
        errors += ValidationError(
            field = "schemaVersion",
            message = "Unbekannte schemaVersion ${params.schemaVersion} (unterstützt: $OPENING_PUT_SCHEMA_VERSION) — Controller-App aktualisieren",
            code = ValidationCode.UNSUPPORTED_SCHEMA,
            messageText = RemoteText(
                de = "Unbekannte schemaVersion ${params.schemaVersion} (unterstützt: $OPENING_PUT_SCHEMA_VERSION) — Controller-App aktualisieren",
                en = "Unknown schemaVersion ${params.schemaVersion} (supported: $OPENING_PUT_SCHEMA_VERSION) — update the controller app",
            ),
        )
    }
    if (params.quantity !in 1..1000) {
        errors += ValidationError(
            "quantity",
            "Anzahl muss zwischen 1 und 1000 liegen",
            messageText = RemoteText(
                de = "Anzahl muss zwischen 1 und 1000 liegen",
                en = "Quantity must be between 1 and 1000",
            ),
        )
    }
    if (options != null && options.accounts.none { it.id == params.accountId }) {
        errors += ValidationError(
            field = "accountId",
            message = "Das gewählte Konto ist nicht (mehr) verfügbar — Auswahl neu laden",
            code = ValidationCode.STALE_OPTION,
            messageText = RemoteText(
                de = "Das gewählte Konto ist nicht (mehr) verfügbar — Auswahl neu laden",
                en = "The selected account is not (or no longer) available — reload the selection",
            ),
        )
    }
    errors += checklistGateErrors(params.ignorePreconditions, params.waitForWindow, options)
    return errors
}
